import { LocationType } from '../../../types/location.type';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NotificationsService } from 'angular2-notifications';
import { EventsHttpService } from '../../../services/events.http.service';
import { UtilsService } from '../../../services/utils.service';
import { EventCreateRequest } from '../../../types/event-request.type';
import { Observable, of, Subscription, switchMap } from 'rxjs';
import { EventResponseEvent } from '../../../types/event-response.type';
import { ActivatedRoute, Router } from '@angular/router';
import { getCategoryOptionsUtil } from '../../../utils/getCategoryOptions.util';
import { Organization } from '../../../types/organization.type';
import { ROUTES } from '../../../constants/routes';
import { DateTimeService } from '../../../services/date-time.service';

interface CreateEventForm {
  event_id?: FormControl<string>;
  event_name: FormControl<string>;
  event_description: FormControl<string>;
  event_location: FormControl<LocationType | undefined>;
  event_start: FormControl<string>;
  event_finish: FormControl<string>;
  categories: FormControl<number[] | undefined>;
  event_organization_id: FormControl<number | undefined>;
  event_registration_open: FormControl<boolean>;
  event_top: FormControl<boolean>;
}

@Component({
  selector: 'app-create-event-legacy',
  templateUrl: './create-event-legacy.component.html',
  styleUrls: ['./create-event-legacy.component.scss'],
})
export class CreateEventLegacyComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();

  public innerForm = new FormGroup<CreateEventForm>({
    event_id: new FormControl('', { nonNullable: true }),
    event_name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    event_description: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    event_location: new FormControl(
      { value: undefined, disabled: false },
      {
        nonNullable: true,
        validators: [Validators.required],
      }
    ),
    event_start: new FormControl(UtilsService.getDate(), {
      nonNullable: true,
      validators: [Validators.required],
    }),
    event_finish: new FormControl(UtilsService.getDate(), {
      nonNullable: true,
      validators: [Validators.required],
    }),
    categories: new FormControl(undefined, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    event_organization_id: new FormControl(undefined, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    event_registration_open: new FormControl(false, {
      nonNullable: true,
    }),
    event_top: new FormControl(false, {
      nonNullable: true,
    }),
  });

  public categoryOptions: any;
  public organizationOptions: any;

  constructor(
    private activatedRoute: ActivatedRoute,
    private notifications: NotificationsService,
    private eventsHttpService: EventsHttpService,
    private router: Router
  ) {}

  public event$: Observable<EventResponseEvent | null> =
    this.activatedRoute.params.pipe(
      switchMap((data) => {
        const eventId: number | null = data['event_id'] ?? null;

        if (eventId) {
          return this.eventsHttpService.getEvent(eventId);
        }

        return of(null);
      })
    );

  ngOnInit(): void {
    this.categoryOptions = getCategoryOptionsUtil(
      this.activatedRoute.snapshot.data['categories']
    );
    this.organizationOptions = (
      this.activatedRoute.snapshot.data['organizations'].data as Organization[]
    ).map((org) => {
      return {
        id: org.organization_id,
        itemName: org.organization_name,
      };
    });

    this.event$.subscribe((event) => {
      if (event) {
        this.innerForm.patchValue({
          ...event,
          event_id: String(event.event_id),
          event_organization_id: event.organization.organization_id,
          event_location: UtilsService.parseLocation(event.event_location),
          categories: event.categories.map((cat) => cat.category_id),
          event_start: DateTimeService.getDateISOString(event.event_start),
          event_finish: DateTimeService.getDateISOString(event.event_finish),
        });
      }
    });

    this.innerForm.get('event_start')?.valueChanges.subscribe((val) => {
      const eventStartDate = this.innerForm.get('event_start');
      const eventFinishDate = this.innerForm.get('event_finish');
      let eventStartDateValue = val;

      if (!eventStartDateValue) {
        eventStartDateValue = UtilsService.getDate();
        eventStartDate?.setValue(eventStartDateValue);
      }

      if (
        !eventFinishDate?.getRawValue() ||
        new Date(eventFinishDate?.getRawValue()).getTime() <
          new Date(eventStartDateValue).getTime()
      ) {
        eventFinishDate?.setValue(eventStartDateValue);
      }
    });

    this.innerForm
      .get('event_finish')
      ?.valueChanges.subscribe((eventFinishDate) => {
        if (!eventFinishDate) {
          const eventStartDate =
            this.innerForm.get('event_start')?.getRawValue() ||
            UtilsService.getDate();

          this.innerForm.get('event_finish')?.setValue(eventStartDate);
        }
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get minEventFinishDate(): string {
    let eventStartDate = this.innerForm.get('event_start')?.getRawValue();

    if (!eventStartDate) {
      eventStartDate = new Date(0);
    }

    return DateTimeService.getDateISOString(eventStartDate, true);
  }

  onEventFinishDateBlur(eventFinishDate: string): void {
    const eventStartDate = this.innerForm.get('event_start')?.getRawValue();

    if (
      !eventStartDate ||
      new Date(eventFinishDate).getTime() < new Date(eventStartDate).getTime()
    ) {
      this.innerForm.get('event_finish')?.setValue(eventStartDate);
    }
  }

  submit(): void {
    const {
      categories,
      event_description,
      event_finish,
      event_location,
      event_start,
      event_organization_id,
      event_name,
      event_id,
      event_top,
      event_registration_open,
    } = this.innerForm.value;

    Object.values(this.innerForm.controls).forEach((control: FormControl) => {
      control.markAsTouched();
      control.updateValueAndValidity();
    });

    if (this.innerForm.invalid || !event_organization_id) {
      return;
    }

    const eventBody: EventCreateRequest = {
      categories: categories ?? [],
      event_description: event_description ?? '',
      event_finish: event_finish ?? '',
      event_location,
      event_name: event_name ?? '',
      event_registration_open: event_registration_open ?? false,
      event_start: event_start ?? '',
      event_top: event_top ?? false,
      event_organization_id,
    };

    this.eventsHttpService
      .saveUpdateEvent(eventBody, event_organization_id, Number(event_id))
      .subscribe(() => {
        this.notifications.success(
          'Success!',
          `Event ${this.innerForm.value.event_name} was successfully created/updated`
        );

        this.router.navigate([ROUTES.CMS_EVENT_MANAGEMENT]);
      });
  }
}
